/* Free Janma Kundli generator — captures lead, calls /api/kundli/free,
   shows AI-generated Vedic report inline + emails it. Major lead magnet. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Stars, Sparkles, ArrowRight, Loader2, Mail } from "lucide-react";
import { api, formatApiErr } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const TIME_OPTIONS = Array.from({ length: 24 * 2 }).map((_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? "00" : "30";
    return `${String(h).padStart(2, "0")}:${m}`;
});

export default function FreeKundli() {
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        birth_date: "", birth_time: "", birth_place: "",
        gender: "other",
    });
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState("");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setReport("");
        try {
            const { data } = await api.post("/kundli/free", form);
            setReport(data.report || "");
            toast.success("Your Kundli is ready — and on its way to your inbox!");
            // scroll to report
            setTimeout(() => {
                document.getElementById("kundli-report")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 200);
        } catch (err) {
            toast.error(formatApiErr(err));
        } finally {
            setLoading(false);
        }
    };

    // Lightweight markdown → HTML for the inline display
    const html = (txt) => {
        const lines = txt
            .replace(/\*\*(.+?)\*\*/g, '<strong class="text-gold">$1</strong>')
            .replace(/^### (.+)$/gm, '<h3 class="font-serif text-xl text-gold mt-6 mb-3">$1</h3>')
            .replace(/^## (.+)$/gm, '<h2 class="font-serif text-2xl text-gold mt-6 mb-3">$1</h2>')
            .replace(/^# (.+)$/gm, '<h2 class="font-serif text-2xl text-gold mt-6 mb-3">$1</h2>');
        return lines
            .split(/\n\n+/)
            .map((p) => p.startsWith("<h") ? p : `<p class="text-cream-muted leading-relaxed mb-4">${p.replace(/\n/g, "<br/>")}</p>`)
            .join("");
    };

    return (
        <div className="pt-32 pb-32">
            <SEO
                title="Free Janma Kundli — Personalized Vedic Report"
                description="Get your free personalized Janma Kundli report by Dr. Tannu Aggarwal. Discover your rashi, nakshatra, lucky colors, gemstone, and one Vedic remedy — instantly."
                path="/free-kundli"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Service",
                    "serviceType": "Free Vedic Kundli Report",
                    "provider": { "@type": "Person", "name": "Dr. Tannu Aggarwal" },
                    "areaServed": "Worldwide",
                    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
                }}
            />

            <div className="container-vv">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    {/* Left: pitch */}
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <span className="gold-line" />
                            <span className="label-eyebrow">100% Free · Instant</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-tight text-balance">
                            Your <span className="italic bg-clip-text text-transparent bg-gold-gradient">Free Janma Kundli</span>, in seconds.
                        </h1>
                        <p className="mt-6 text-cream-muted leading-relaxed">
                            Enter your birth details and receive a personalized Vedic report — generated by Dr. Tannu's astrology AI assistant.
                            Includes your Janma Rashi, Nakshatra hint, personality core, career & relationship insights, lucky gemstone & color, and one simple Vedic remedy.
                        </p>

                        <ul className="mt-8 space-y-3 text-sm text-cream">
                            {[
                                "Your moon sign (Janma Rashi) revealed",
                                "Career, love & health snapshot",
                                "Lucky color, day, gemstone & number",
                                "One personalized Vedic remedy",
                                "Delivered to your inbox in a beautiful report",
                            ].map((b) => (
                                <li key={b} className="flex items-start gap-2">
                                    <Sparkles className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10 rounded-2xl border border-gold/20 bg-ink-deep/60 p-5">
                            <p className="text-xs text-gold uppercase tracking-widest mb-2">Want a deeper read?</p>
                            <p className="text-sm text-cream-muted leading-relaxed">
                                For a full personalized chart analysis with dashas, BNN insights, and a 45-min 1:1 session, book a paid consultation. ₹3,100 onwards.
                            </p>
                            <Link to="/services" className="mt-4 inline-flex items-center gap-2 text-sm text-gold hover:gap-3 transition-all">
                                See all consultations <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="rounded-3xl border border-gold/30 bg-ink-deep p-6 sm:p-8 shadow-xl">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="h-12 w-12 rounded-full bg-gold/10 border border-gold/30 grid place-items-center">
                                <Stars className="h-6 w-6 text-gold" />
                            </div>
                            <div>
                                <div className="font-serif text-xl text-cream">Enter birth details</div>
                                <div className="text-xs text-cream-muted">More accurate = better insights</div>
                            </div>
                        </div>

                        <form onSubmit={submit} className="space-y-3" data-testid="free-kundli-form">
                            <input required placeholder="Full Name" value={form.name} onChange={update("name")} className="input-luxe" data-testid="kundli-name" />
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input required type="email" placeholder="Email" value={form.email} onChange={update("email")} className="input-luxe" data-testid="kundli-email" />
                                <input placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} className="input-luxe" data-testid="kundli-phone" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input required type="date" value={form.birth_date} onChange={update("birth_date")} className="input-luxe" data-testid="kundli-bdate" />
                                <select required value={form.birth_time} onChange={update("birth_time")} className="input-luxe" data-testid="kundli-btime">
                                    <option value="">Birth Time</option>
                                    {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <input required placeholder="Birth Place (City, Country)" value={form.birth_place} onChange={update("birth_place")} className="input-luxe" data-testid="kundli-place" />
                            <select value={form.gender} onChange={update("gender")} className="input-luxe" data-testid="kundli-gender">
                                <option value="other">Prefer not to say</option>
                                <option value="female">Female</option>
                                <option value="male">Male</option>
                            </select>

                            <button type="submit" disabled={loading} className="btn-gold w-full mt-2 disabled:opacity-50" data-testid="kundli-submit">
                                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating your Kundli...</> : <>Generate Free Kundli <ArrowRight className="h-4 w-4" /></>}
                            </button>
                            <p className="text-[11px] text-cream-dim text-center">By submitting you'll also receive our monthly horoscope newsletter. Unsubscribe anytime.</p>
                        </form>
                    </div>
                </div>

                {report && (
                    <div id="kundli-report" className="mt-20 rounded-3xl border border-gold/30 bg-ink-deep p-8 sm:p-12 max-w-4xl mx-auto" data-testid="kundli-report">
                        <div className="flex items-center gap-3 mb-6">
                            <Sparkles className="h-5 w-5 text-gold" />
                            <span className="label-eyebrow">Your Janma Kundli</span>
                        </div>
                        <div className="prose-vedic" dangerouslySetInnerHTML={{ __html: html(report) }} />
                        <div className="mt-8 rounded-2xl border border-gold/30 bg-gold/5 p-5 flex items-start gap-3">
                            <Mail className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-cream">
                                A copy has been emailed to <strong>{form.email}</strong>. Want a 45-min deep-dive with Dr. Tannu?
                                <Link to="/services" className="ml-1 text-gold hover:underline">Book a paid session →</Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
